import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Search,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/AppLayout";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { downloadExport } from "@/lib/exportHelpers";

const TABS = [
  { value: "submitted", label: "Awaiting QC", color: "bg-purple-50 text-purple-700" },
  { value: "approved", label: "Approved", color: "bg-emerald-50 text-emerald-700" },
  { value: "rejected", label: "Rejected", color: "bg-rose-50 text-rose-700" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export default function ReviewedQueue() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("submitted");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ submitted: 0, approved: 0, rejected: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("");
  const [rating, setRating] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [reviewerFilter, setReviewerFilter] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmState, setConfirmState] = useState(null);

  // Load filter dropdown options once
  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: t }, { data: r }] = await Promise.all([
          api.get("/questions/subjects"),
          api.get("/tags"),
          api.get("/users/reviewers"),
        ]);
        setSubjects(s);
        setTags(t);
        setReviewers(r);
      } catch (e) {
        console.error("Failed to load QC filter options:", e);
        /* filters will just be empty if this fails */
      }
    })();
  }, []);

  const loadCounts = useCallback(async () => {
    try {
      const [s, a, r] = await Promise.all([
        api.get("/questions", { params: { status: "submitted", page_size: 1 } }),
        api.get("/questions", { params: { status: "approved", page_size: 1 } }),
        api.get("/questions", { params: { status: "rejected", page_size: 1 } }),
      ]);
      setCounts({
        submitted: s.data.total,
        approved: a.data.total,
        rejected: r.data.total,
      });
    } catch (e) {
      console.error("Failed to load QC tab counts:", e);
      /* best-effort */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status, page, page_size: pageSize };
      if (search) params.search = search;
      if (subject) params.subject = subject;
      if (rating) params.rating = Number(rating);
      if (tagFilter) params.tags = tagFilter;
      if (reviewerFilter) params.assigned_to = reviewerFilter;
      const { data } = await api.get("/questions", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [status, page, pageSize, search, subject, rating, tagFilter, reviewerFilter]);

  useEffect(() => {
    load();
    loadCounts();
  }, [load, loadCounts]);

  useEffect(() => {
    // clear selection on tab/page/search/filters change
    setSelected(new Set());
  }, [status, page, pageSize, search, subject, rating, tagFilter, reviewerFilter]);

  const act = async (qid, action) => {
    setBusyId(qid);
    try {
      await api.post(`/questions/${qid}/qc`, { action });
      const messages = { approve: "Approved", reject: "Rejected", resend: "Sent back to reviewer" };
      toast.success(messages[action] || "Done");
      load();
      loadCounts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const toggleSelect = (qid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid);
      else next.add(qid);
      return next;
    });
  };

  const toggleAll = () => {
    if (items.length > 0 && items.every((i) => selected.has(i.id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((i) => i.id)));
    }
  };

  const bulkAct = (action) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setConfirmState({
      title: `${action[0].toUpperCase() + action.slice(1)} ${ids.length} question${ids.length === 1 ? "" : "s"}?`,
      message: (
        <span>
          You're about to <strong>{action}</strong> {ids.length} question
          {ids.length === 1 ? "" : "s"} at once.
        </span>
      ),
      confirmLabel: `${action[0].toUpperCase() + action.slice(1)} ${ids.length}`,
      danger: action === "reject",
      onConfirm: async () => {
        setBulkBusy(true);
        try {
          const { data } = await api.post("/questions/qc-bulk", {
            question_ids: ids,
            action,
          });
          toast.success(`${action[0].toUpperCase() + action.slice(1)}ed ${data.updated}`);
          setSelected(new Set());
          load();
          loadCounts();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };

  const onExport = (mode = "new") => downloadExport(mode);

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <PageContainer>
      <PageHeader
        title="Quality Control"
        subtitle="Approve, reject, or send submitted questions back for re-review"
        actions={
          <button
            data-testid="qc-export-btn"
            onClick={() => onExport("new")}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold"
            title="Export newly approved questions (visit Exports page for more options)"
          >
            <ShieldCheck className="w-4 h-4" /> Export New
          </button>
        }
      />

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-100 brand-shadow p-1.5 mb-4 inline-flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.value}
            data-testid={`qc-tab-${t.value}`}
            onClick={() => {
              setStatus(t.value);
              setPage(1);
            }}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
              status === t.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t.label}
            <span
              className={`ml-2 px-1.5 py-0.5 rounded-md text-[10px] font-mono ${
                status === t.value ? "bg-white/20" : t.color
              }`}
            >
              {counts[t.value]}
            </span>
          </button>
        ))}
      </div>

      {/* Search + page size */}
      <div className="bg-white rounded-xl border border-slate-100 brand-shadow p-3 mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="qc-search"
            type="text"
            placeholder="Search question text…"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-mono text-slate-500">
          Per page
          <select
            data-testid="qc-page-size"
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(Number(e.target.value));
            }}
            className="px-2 py-1.5 rounded-md border border-slate-200 text-sm bg-white outline-none focus:border-orange-500"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Filter row: subject / rating / tag / reviewer */}
      <div className="bg-white rounded-xl border border-slate-100 brand-shadow p-3 mb-3 flex flex-wrap items-center gap-2">
        <select
          data-testid="qc-subject-filter"
          value={subject}
          onChange={(e) => {
            setPage(1);
            setSubject(e.target.value);
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none max-w-[260px]"
        >
          <option value="">All subjects</option>
          {subjects.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          data-testid="qc-rating-filter"
          value={rating}
          onChange={(e) => {
            setPage(1);
            setRating(e.target.value);
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
        >
          <option value="">All ratings</option>
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>
              Rating {n}
            </option>
          ))}
        </select>
        <select
          data-testid="qc-tag-filter"
          value={tagFilter}
          onChange={(e) => {
            setPage(1);
            setTagFilter(e.target.value);
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none max-w-[220px]"
        >
          <option value="">All tags</option>
          {tags.map((t) => (
            <option key={t.id} value={t.name}>
              {t.name}
            </option>
          ))}
        </select>
        <select
          data-testid="qc-reviewer-filter"
          value={reviewerFilter}
          onChange={(e) => {
            setPage(1);
            setReviewerFilter(e.target.value);
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none max-w-[220px]"
        >
          <option value="">All reviewers</option>
          {reviewers.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name || r.email}
            </option>
          ))}
        </select>
        {(subject || rating || tagFilter || reviewerFilter) && (
          <button
            data-testid="qc-clear-filters"
            onClick={() => {
              setSubject("");
              setRating("");
              setTagFilter("");
              setReviewerFilter("");
              setPage(1);
            }}
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-mono text-slate-600 hover:bg-slate-50"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          data-testid="qc-bulk-bar"
          className="mb-3 flex flex-wrap items-center gap-2 p-3 rounded-xl bg-orange-50 border border-orange-200"
        >
          <span className="text-sm font-semibold text-orange-900 mr-2">
            {selected.size} selected
          </span>
          {status === "submitted" && (
            <>
              <button
                data-testid="qc-bulk-approve"
                onClick={() => bulkAct("approve")}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-semibold"
              >
                <ThumbsUp className="w-3.5 h-3.5" /> Approve Selected
              </button>
              <button
                data-testid="qc-bulk-reject"
                onClick={() => bulkAct("reject")}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white text-xs font-semibold"
              >
                <ThumbsDown className="w-3.5 h-3.5" /> Reject Selected
              </button>
              <button
                data-testid="qc-bulk-resend"
                onClick={() => bulkAct("resend")}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 text-xs font-semibold disabled:opacity-50"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Resend
              </button>
            </>
          )}
          {status !== "submitted" && (
            <button
              data-testid="qc-bulk-resend"
              onClick={() => bulkAct("resend")}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-semibold"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Send back for re-review
            </button>
          )}
          <button
            onClick={() => setSelected(new Set())}
            data-testid="qc-bulk-clear"
            className="ml-auto text-xs text-orange-700 hover:underline font-semibold"
          >
            Clear
          </button>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-xl border border-slate-100 brand-shadow overflow-hidden">
        {items.length > 0 && (
          <div className="px-5 py-2.5 border-b border-slate-100 bg-slate-50/60 flex items-center gap-3">
            <input
              type="checkbox"
              checked={items.length > 0 && items.every((i) => selected.has(i.id))}
              onChange={toggleAll}
              data-testid="qc-select-all"
              className="rounded border-slate-300 text-orange-500 focus:ring-orange-500/20"
            />
            <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
              Select all on page
            </span>
          </div>
        )}

        {loading ? (
          <div className="px-6 py-16 text-center text-sm text-slate-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <div className="text-sm text-slate-500">
              No <strong>{status}</strong> questions
              {search ? ` matching “${search}”` : ""}.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-50">
            {items.map((q) => (
              <QcRow
                key={q.id}
                q={q}
                status={status}
                busy={busyId === q.id}
                selected={selected.has(q.id)}
                onToggle={() => toggleSelect(q.id)}
                onAct={(action) => act(q.id, action)}
                onOpen={() => navigate(`/batches/${q.batch_id}`)}
              />
            ))}
          </ul>
        )}

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500 font-mono">
          <div>
            Page {page} of {pages} · {total} total
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page === pages}
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </PageContainer>
  );
}

function QcRow({ q, status, busy, selected, onToggle, onAct, onOpen }) {
  return (
    <li
      data-testid={`qc-row-${q.id}`}
      className={`px-5 py-4 flex items-start gap-3 hover:bg-slate-50/60 ${
        selected ? "bg-orange-50/40" : ""
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        data-testid={`qc-select-${q.id}`}
        className="mt-1 rounded border-slate-300 text-orange-500 focus:ring-orange-500/20"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-slate-100 text-slate-600">
            ID {q.source_id || "—"}
          </span>
          {q.subject && (
            <span className="px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-slate-100 text-slate-600">
              {q.subject}
            </span>
          )}
          {q.raw_rating != null && (
            <span
              data-testid={`qc-raw-rating-${q.id}`}
              title="Raw rating from uploaded sheet"
              className="px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-slate-100 text-slate-700"
            >
              Raw {q.raw_rating}
            </span>
          )}
          {q.rating && (
            <span className="px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-orange-50 text-orange-700 font-semibold">
              Rating {q.rating}
            </span>
          )}
          <span className="text-[10px] font-mono text-slate-400">
            by {q.reviewer_name || "—"}
          </span>
        </div>
        <div className="text-sm text-slate-800 line-clamp-2">{q.question_text}</div>
        {q.notes && (
          <div className="mt-1 text-[11px] text-slate-500 italic line-clamp-1">
            Note: {q.notes}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={onOpen}
          className="px-2.5 py-1 rounded-md text-xs font-semibold text-slate-600 hover:bg-slate-100"
        >
          Open
        </button>
        {status === "submitted" && (
          <>
            <button
              data-testid={`qc-approve-${q.id}`}
              onClick={() => onAct("approve")}
              disabled={busy}
              title="Approve"
              className="p-2 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
            >
              <ThumbsUp className="w-4 h-4" />
            </button>
            <button
              data-testid={`qc-reject-${q.id}`}
              onClick={() => onAct("reject")}
              disabled={busy}
              title="Reject"
              className="p-2 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50"
            >
              <ThumbsDown className="w-4 h-4" />
            </button>
            <button
              data-testid={`qc-resend-${q.id}`}
              onClick={() => onAct("resend")}
              disabled={busy}
              title="Send back for re-review"
              className="p-2 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </>
        )}
        {status !== "submitted" && (
          <button
            data-testid={`qc-resend-${q.id}`}
            onClick={() => onAct("resend")}
            disabled={busy}
            title="Send back for re-review"
            className="p-2 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
      </div>
    </li>
  );
}
